import { useState, useEffect } from 'react';
import type { GalleryImage } from '../types';

type LightboxProps = {
  images: GalleryImage[];
  isOpen: boolean;
  initialIndex?: number;
  onClose: () => void;
};

function Lightbox({ images, isOpen, initialIndex = 0, onClose }: LightboxProps) {
  const [current, setCurrent] = useState(initialIndex);

  useEffect(() => {
    setCurrent(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setCurrent((c) => (c + 1) % images.length);
      if (e.key === 'ArrowLeft') setCurrent((c) => (c - 1 + images.length) % images.length);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose, images.length]);

  if (!isOpen) return null;

  const img = images[current];

  return (
    <div
      className="fixed inset-0 z-[100] bg-foreground-950/96 flex items-center justify-center"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl px-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative overflow-hidden rounded-xl aspect-[16/10] bg-foreground-900 w-full h-full">
          <img
            src={img.src}
            alt={img.alt}
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
        </div>

        <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-background-100/70 text-sm">{img.caption}</p>
          <div className="flex items-center justify-between gap-4 sm:justify-end">
            <p className="text-background-100/50 text-sm">{current + 1} / {images.length}</p>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close photo gallery"
              className="inline-flex items-center gap-2 rounded-lg bg-[#EFE4D6] text-foreground-950 px-4 py-2 text-sm font-medium cursor-pointer hover:bg-[#E9DCCB] transition-colors"
            >
              <i className="ri-close-line" />
              Close
            </button>
          </div>
        </div>

        <button
          type="button"
          onClick={() => setCurrent((c) => (c - 1 + images.length) % images.length)}
          className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-background-50 bg-foreground-950/60 hover:bg-foreground-950/80 cursor-pointer rounded-r-xl transition-colors"
          aria-label="Previous photo"
        >
          <i className="ri-arrow-left-line text-lg" />
        </button>
        <button
          type="button"
          onClick={() => setCurrent((c) => (c + 1) % images.length)}
          className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center text-background-50 bg-foreground-950/60 hover:bg-foreground-950/80 cursor-pointer rounded-l-xl transition-colors"
          aria-label="Next photo"
        >
          <i className="ri-arrow-right-line text-lg" />
        </button>
      </div>
    </div>
  );
}

type GalleryGridProps = {
  images: GalleryImage[];
  propertyName: string;
  onRequestOpen: (index: number) => void;
};

export function GalleryGrid({ images, propertyName, onRequestOpen }: GalleryGridProps) {
  return (
    <section className="bg-background-100/60 py-16 md:py-24 border-y border-background-200/70">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex items-end justify-between mb-8 md:mb-10">
          <div>
            <h2 className="font-heading text-foreground-950 text-3xl md:text-4xl leading-tight tracking-[-0.01em]">
              Inside {propertyName}
            </h2>
          </div>
          <button
            type="button"
            onClick={() => onRequestOpen(0)}
            className="hidden md:inline-flex items-center gap-2 text-sm text-foreground-700 hover:text-foreground-950 cursor-pointer whitespace-nowrap"
          >
            View all photos
            <i className="ri-external-link-line" />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-2 md:gap-3 lg:items-stretch">
          <button
            type="button"
            onClick={() => onRequestOpen(0)}
            className="lg:col-span-7 relative overflow-hidden rounded-xl cursor-pointer group aspect-[4/3]"
          >
            <img
              src={images[0].src}
              alt={images[0].alt}
              className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/15 transition-colors" />
          </button>

          <div className="lg:col-span-5 grid grid-cols-2 lg:grid-rows-2 gap-2 md:gap-3">
            {images.slice(1, 5).map((img, i) => (
              <button
                key={img.src}
                type="button"
                onClick={() => onRequestOpen(i + 1)}
                className="relative overflow-hidden rounded-xl cursor-pointer group aspect-[4/3] lg:aspect-auto lg:h-full"
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  className="w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/15 transition-colors" />
              </button>
            ))}
          </div>

          {images[5] && (
            <button
              type="button"
              onClick={() => onRequestOpen(5)}
              className="sm:col-span-2 lg:col-span-12 relative overflow-hidden rounded-xl cursor-pointer group"
              style={{ aspectRatio: '21/7' }}
            >
              <img
                src={images[5].src}
                alt={images[5].alt}
                className="w-full h-full object-cover object-center object-top transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-foreground-950/0 group-hover:bg-foreground-950/15 transition-colors" />
            </button>
          )}
        </div>

        <div className="mt-7 flex justify-center">
          <button
            type="button"
            onClick={() => onRequestOpen(0)}
            className="inline-flex items-center justify-center gap-2 bg-[#EFE4D6] text-foreground-950 px-5 py-3 rounded-lg text-sm font-medium cursor-pointer hover:bg-[#E9DCCB] transition-colors whitespace-nowrap"
          >
            See all photos
            <span className="text-foreground-600">({images.length})</span>
            <i className="ri-gallery-line" />
          </button>
        </div>
      </div>
    </section>
  );
}

export function GalleryLightbox({ images, isOpen, initialIndex, onClose }: LightboxProps) {
  return <Lightbox images={images} isOpen={isOpen} initialIndex={initialIndex} onClose={onClose} />;
}
